import ajax from './ajax';

export interface TimeSeriesData {
    date: string;
    value: number;
    label?: string;
}

export interface ExamAnalytics {
    examId: string;
    title: string;
    totalAttempts: number;
    averageScore: number;
    passRate: number;
    totalParticipants: number;
    highestScore: number;
    lowestScore: number;
    medianScore: number;
    averageTime: number;
    questionAnalytics: QuestionAnalyticsItem[];
    timeSeriesData?: TimeSeriesData[];
}

export interface QuestionAnalyticsItem {
    questionId: string;
    questionText: string;
    answerDistribution: AnswerDistribution[];
    correctAnswerPercentage: number;
    averageTime: number;
    difficulty: string;
    discrimination: number;
}

export interface AnswerDistribution {
    option: string;
    count: number;
    percentage: number;
}

export interface QuestionAnalytics {
    questionId: string;
    questionText: string;
    totalAttempts: number;
    correctAnswers: number;
    correctPercentage: number;
    averageTime: number;
    difficulty: string;
    discrimination: number;
    answerDistribution: AnswerDistribution[];
    examsUsedIn: string[];
}

export interface UserAnalytics {
    userId: string;
    userName: string;
    totalExamsTaken: number;
    averageScore: number;
    totalTimeSpent: number;
    strengths: string[];
    weaknesses: string[];
    recentAttempts: UserAttemptSummary[];
    performanceTrend: PerformanceTrendData[];
}

export interface UserAttemptSummary {
    attemptId: string;
    examId: string;
    examTitle: string;
    score: number;
    totalMarks: number;
    percentage: number;
    timeSpent: number;
    status: string;
    attemptedAt: string;
}

export interface PerformanceTrendData {
    date: string;
    averageScore: number;
    examsAttempted: number;
}

export interface OrganizationAnalytics {
    organizationId: string;
    organizationName: string;
    totalUsers: number;
    totalExams: number;
    totalAttempts: number;
    averageUserScore: number;
    userEngagement: number;
    topPerformers: UserPerformanceSummary[];
    examsWithHighestParticipation: ExamParticipationSummary[];
    departmentAnalytics?: DepartmentAnalytics[];
}

export interface UserPerformanceSummary {
    userId: string;
    userName: string;
    email: string;
    averageScore: number;
    totalExamsTaken: number;
    rank: number;
}

export interface ExamParticipationSummary {
    examId: string;
    examTitle: string;
    participantCount: number;
    averageScore: number;
    completionRate: number;
}

export interface DepartmentAnalytics {
    departmentId: string;
    departmentName: string;
    userCount: number;
    averageScore: number;
    totalAttempts: number;
}

export interface DashboardStats {
    totalUsers: number;
    totalExams: number;
    totalAttempts: number;
    totalQuestions: number;
    activeUsers: number;
    examsInProgress: number;
    averageExamScore: number;
    systemHealthStatus: string;
    recentActivity: ActivityLog[];
}

export interface ActivityLog {
    activityId: string;
    type: string;
    description: string;
    userId: string;
    userName: string;
    timestamp: string;
}

class AnalyticsService {
    async getSystemStats(): Promise<any> {
        return ajax.get('/api/analytics/system');
    }

    async getExamAnalytics(examId: string): Promise<ExamAnalytics> {
        return ajax.get(`/api/analytics/exams/${examId}`);
    }

    async getQuestionAnalytics(questionId: string): Promise<QuestionAnalytics> {
        return ajax.get(`/api/analytics/questions/${questionId}`);
    }

    async getUserAnalytics(userId: string): Promise<UserAnalytics> {
        return ajax.get(`/api/analytics/users/${userId}`);
    }

    async getOrganizationAnalytics(orgId: string): Promise<OrganizationAnalytics> {
        return ajax.get(`/api/analytics/organizations/${orgId}`);
    }

    async getDashboardStats(): Promise<DashboardStats> {
        return ajax.get('/api/analytics/dashboard');
    }

    async getInstructorStats(): Promise<any> {
        return ajax.get('/api/analytics/instructor/stats');
    }

    async getOrgAdminStats(): Promise<any> {
        return ajax.get('/api/analytics/org-admin/stats');
    }

    async getStudentStats(): Promise<any> {
        return ajax.get('/api/analytics/student/stats');
    }
}

export default new AnalyticsService();
